import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Divider,
    Stack,
    Checkbox,
    Tooltip,
    Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";

const LS_KEY = "news_popup_last_seen_version";

const NEWS = {
    slides: [
        {
            image: "https://www.trucarbon.co/wp-content/uploads/2026/07/20260709_161041-1.jpg-1-1024x577.jpeg",
            imagePosition: "0% 70%",
            title: "SRUK: Indonesia’s New Carbon Unit Registry Explained",
            body: "See how Indonesia’s national registry tracks and manages carbon credits.",
            ctaText: "Read More",
            ctaHref: "https://www.trucarbon.co/sruk-carbon-unit-registry-explained",
        },
        {
            image: "https://www.trucarbon.co/wp-content/uploads/2026/03/access-trumrv-1.webp",
            imagePosition: "0% 60%",
            title: "TruCarbon Presents TruMRV",
            body: "Built to streamline the monitoring, reporting, and verification (MRV) process for carbon projects.",
            ctaText: "Check Out TruMRV",
            ctaHref: "https://trucarbon.co/trumrv",
        },
    ],
    newsItems: [
        {
            id: 1,
            title: "What Are PSPK 1 and PSPK 2? Indonesia’s New Sustainability Disclosure Standards Explained",
            summary: "Indonesia moves from voluntary narrative reporting to standardized sustainability disclosure requirements.",
            dateISO: "2026-08-14",
            href: "https://www.trucarbon.co/what-are-pspk-1-and-pspk-2",
            tag: "Knowledge",
        },
        {
            id: 2,
            title: "What Makes a High-Quality Carbon Credit? Key Principles Every Buyer Should Understand",
            summary: "The fundamental characteristics that define high-quality carbon credits for buyers.",
            dateISO: "2026-07-27",
            href: "https://www.trucarbon.co/what-makes-a-high-quality-carbon-credits",
            tag: "Knowledge",
        },
        {
            id: 3,
            title: "Before You Buy Carbon Credits in Indonesia: What Every International Buyer Needs to Know",
            summary: "Essential guidance for international buyers navigating Indonesia’s carbon credit market.",
            dateISO: "2026-07-27",
            href: "https://www.trucarbon.co/before-buying-carbon-credits-in-indonesia",
            tag: "Knowledge",
        },
    ],
    patchNotes: [
        {
            version: "1.1.4",
            dateISO: "2026-08-31",
            notes: [
                "Tidier emission factor selector",
                "Improved emission source list in bulk import",
            ],
        },
        {
            version: "1.1.3",
            dateISO: "2026-07-06",
            notes: [
                "Stronger password rules on change and reset",
                "Password strength meter and clearer error hints",
                "Password rotation reminder on the Dashboard",
            ],
        },
        {
            version: "1.1.2",
            dateISO: "2026-06-30",
            notes: [
                "Updated authentication",
            ],
        },
        {
            version: "1.1.1",
            dateISO: "2026-06-19",
            notes: [
                "Security hardening improvements",
                "Updated session timeout rules",
                "Improved session handling for safer access",
            ],
        },
    ],
};

function getIsSuppressed(version) {
    try {
        return localStorage.getItem(LS_KEY) === String(version || "");
    } catch {
        return false;
    }
}

export function shouldOpenNewsPopup(version) {
    try {
        const last = localStorage.getItem(LS_KEY);
        return last !== String(version || "");
    } catch (_) {
        return true;
    }
}

function useCarousel({ size, autoAdvanceMs = 6000, isActive = true }) {
    const [index, setIndex] = useState(0);
    const timerRef = useRef(null);

    const next = useCallback(() => setIndex((i) => (i + 1) % Math.max(size, 1)), [size]);
    const prev = useCallback(() => setIndex((i) => (i - 1 + Math.max(size, 1)) % Math.max(size, 1)), [size]);
    const goto = useCallback((i) => setIndex(((i % size) + size) % size), [size]);

    useEffect(() => {
        if (!isActive || size <= 1) return;
        timerRef.current && clearInterval(timerRef.current);
        timerRef.current = setInterval(() => next(), autoAdvanceMs);
        return () => timerRef.current && clearInterval(timerRef.current);
    }, [size, autoAdvanceMs, isActive, next]);

    return { index, next, prev, goto };
}

function formatDate(d) {
    if (!d) return "";
    try {
        return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return d;
    }
}

export default function NewsPopup({
    version = "1.1.4",
    open: openProp,
    onClose,
    slides = NEWS.slides,
    newsItems = NEWS.newsItems,
    patchNotes = NEWS.patchNotes,
    autoAdvanceMs = 6000,
    showDontShowToggle = true,
    PaperProps: paperPropsOverride,
    onSuppressChange,
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(Boolean(openProp));
    const [dontShow, setDontShow] = useState(getIsSuppressed(version));
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    useEffect(() => setOpen(Boolean(openProp)), [openProp]);

    useEffect(() => {
        if (open) {
            const suppressed = getIsSuppressed(version);
            setDontShow(suppressed);
            if (typeof onSuppressChange === "function") onSuppressChange(suppressed); // ⬅️ NEW
        }
    }, [open, version, onSuppressChange]);

    const handleToggleSuppress = (checked) => {
        setDontShow(checked);
        try {
            if (checked) {
                localStorage.setItem(LS_KEY, String(version || ""));
            } else if (localStorage.getItem(LS_KEY) === String(version || "")) {
                localStorage.removeItem(LS_KEY);
            }
        } catch { }
        if (typeof onSuppressChange === "function") onSuppressChange(checked);
    };

    const { index, next, prev, goto } = useCarousel({ size: slides.length, autoAdvanceMs, isActive: open });

    const handleClose = useCallback(() => {
        setOpen(false);
        try {
            if (dontShow) {
                // Save this version as suppressed
                localStorage.setItem(LS_KEY, String(version || ""));
            } else {
                // If user unchecked it, remove suppression for this version
                if (localStorage.getItem(LS_KEY) === String(version || "")) {
                    localStorage.removeItem(LS_KEY);
                }
            }
        } catch { }
        onClose && onClose();
    }, [onClose, dontShow, version]);

    const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
    const onTouchEnd = (e) => {
        touchEndX.current = e.changedTouches[0].clientX;
        const dx = touchEndX.current - touchStartX.current;
        if (Math.abs(dx) > 40) {
            if (dx < 0) next(); else prev();
        }
    };

    const paperProps = useMemo(() => ({
        sx: {
            width: { xs: "100%", sm: "90%", md: "860px", lg: "980px" },
            maxWidth: "unset",
            background: "linear-gradient(180deg, #F7FBF9 0%, #FFFFFF 60%)",
            borderRadius: 3,
            boxShadow: (theme) => theme.shadows[12],
        },
        ...paperPropsOverride,
    }), [paperPropsOverride]);

    return (
        <Dialog open={open} onClose={handleClose} PaperProps={paperProps} maxWidth={false}>
            <Box
                sx={{
                    p: 2,
                    pl: 3,
                    pr: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                }}
            >
                {/* Left: title + version chip */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
                >
                    <Typography variant="h6" fontWeight={700} noWrap>
                        {t("newsPopUp.title")}{version ? ` — v${version}` : ""}
                    </Typography>
                    {version && (
                        <Chip size="small" label={`v${version}`} color="default" />
                    )}
                </Stack>

                {/* Middle: Don't show toggle (caption-sized, compact) */}
                {showDontShowToggle && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{
                            mr: 0.5,
                            maxWidth: { xs: 200, sm: 280 },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        <Checkbox
                            size="small"
                            checked={dontShow}
                            onChange={(e) => handleToggleSuppress(e.target.checked)}
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                lineHeight: 1.2,
                                userSelect: "none",
                                // Shorten label on very small screens
                                display: { xs: "none", sm: "inline" },
                            }}
                        >
                            {t("newsPopUp.dontShowThisAgain")} v{version}
                        </Typography>
                    </Stack>
                )}

                {/* Divider + Close */}
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, my: 0.5, opacity: 0.4 }}
                />
                <Tooltip title="Close">
                    <IconButton onClick={handleClose} aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </Box>
            <Divider />

            <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, maxHeight: '85vh', overflowY: 'auto' }}>
                {/* Compact wide carousel */}
                <Typography variant="overline" sx={{ letterSpacing: 0.8, color: 'text.secondary' }}>{t("newsPopUp.highlights")}</Typography>
                {slides.length ? (
                    <Box sx={{ position: 'relative', width: '100%', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                        <Box
                            onTouchStart={onTouchStart}
                            onTouchEnd={onTouchEnd}
                            sx={{ position: 'relative', width: '100%', height: { xs: 180, sm: 200, md: 220 } }}
                        >
                            <Box sx={{ width: '100%', height: '100%' }}>
                                <SlideCard slide={slides[index]} compact />
                            </Box>
                            {slides.length > 1 && (
                                <>
                                    <NavButton side="left" onClick={prev} ariaLabel="Previous slide" />
                                    <NavButton side="right" onClick={next} ariaLabel="Next slide" />
                                </>
                            )}
                        </Box>
                        {slides.length > 1 && (
                            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
                                {slides.map((_, i) => <Dot key={i} active={i === index} onClick={() => goto(i)} />)}
                            </Stack>
                        )}
                    </Box>
                ) : (
                    <EmptyState title="No highlights yet" subtitle="Add slides to showcase updates." />
                )}

                <Divider sx={{ my: 2 }} />

                {/* News */}
                <Typography variant="overline" sx={{ letterSpacing: 0.8, color: 'text.secondary' }}>{t("newsPopUp.news")}</Typography>
                {newsItems.length ? (
                    <List sx={{ width: '100%' }}>
                        {newsItems.map((n, idx) => (
                            <React.Fragment key={n.id ?? idx}>
                                <ListItem disablePadding alignItems="flex-start">
                                    <ListItemButton component="a" href={n.href || undefined} target="_blank" rel="noreferrer">
                                        <ListItemText
                                            primary={<Stack direction="row" spacing={1}><Typography variant="subtitle1" fontWeight={700}>{n.title}</Typography>{n.tag && <Chip label={n.tag} size="small" />}</Stack>}
                                            secondary={<Typography variant="body2" color="text.secondary">{n.summary} {formatDate(n.dateISO)}</Typography>}
                                        />
                                    </ListItemButton>
                                </ListItem>
                                {idx < newsItems.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                ) : (
                    <EmptyState title="No news" subtitle="Stay tuned for updates." />
                )}

                <Divider sx={{ my: 2 }} />

                {/* Patch notes */}
                <Typography variant="overline" sx={{ letterSpacing: 0.8, color: 'text.secondary' }}>{t("newsPopUp.patchNotes")}</Typography>
                {patchNotes.length ? (
                    <Stack spacing={2}>
                        {patchNotes.map((p) => (
                            <Paper key={p.version} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle1" fontWeight={700}>v{p.version}</Typography>
                                    {p.dateISO && <Typography variant="caption" color="text.secondary">{formatDate(p.dateISO)}</Typography>}
                                </Stack>
                                <List dense sx={{ mt: 1 }}>
                                    {p.notes.map((note, i) => (
                                        <ListItem key={i} sx={{ py: 0 }}>
                                            <ListItemText primary={<Typography variant="body2">• {note}</Typography>} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        ))}
                    </Stack>
                ) : (
                    <EmptyState title="No patch notes" subtitle="Coming soon." />
                )}
            </Box>
        </Dialog>
    );
}

function NavButton({ side, onClick, ariaLabel }) {
    const isLeft = side === 'left';
    return (
        <IconButton
            aria-label={ariaLabel}
            onClick={onClick}
            sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [isLeft ? 'left' : 'right']: 8, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }, boxShadow: 1 }}
        >
            {isLeft ? <ArrowBackIosNewIcon /> : <ArrowForwardIosIcon />}
        </IconButton>
    );
}

function Dot({ active, onClick }) {
    return <Box onClick={onClick} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: active ? 'text.primary' : 'action.disabled', opacity: active ? 0.9 : 0.6, cursor: 'pointer' }} />;
}

function EmptyState({ title, subtitle }) {
    return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>}
        </Box>
    );
}

function SlideCard({ slide }) {
    const { image, imagePosition, title, body, ctaText, ctaHref } = slide || {};
    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            {image && <Box component="img" src={image} alt={title || 'slide'} sx={{ width: '100%', objectFit: 'cover', objectPosition: imagePosition || "center center" }} />}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', bgcolor: 'rgba(0,0,0,0.45)', color: 'white', p: 1.5 }}>
                {title && <Typography variant="title" fontWeight={700}>{title}</Typography>}
                {body && <Typography variant="body2" sx={{ opacity: 0.9 }}>{body}</Typography>}
                {ctaHref && <Button size="small" variant="contained" href={ctaHref} target="_blank" rel="noreferrer" sx={{ mt: 1 }}>{ctaText || 'Learn more'}</Button>}
            </Box>
        </Box>
    );
}
